import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Inbox, Loader2, RefreshCw } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { GradientButton } from "../components/primitives";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";

const STATUS_COLORS = {
  new: "bg-brand/20 text-brand-gold",
  contacted: "bg-blue-500/20 text-blue-300",
  won: "bg-emerald-500/20 text-emerald-300",
  archived: "bg-white/10 text-white/50",
};

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inquiries, setInquiries] = useState([]);

  const load = async (adminKey) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/inquiries", { headers: { "X-Admin-Key": adminKey } });
      setInquiries(res.data);
      setAuthed(true);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Invalid admin key." : "Failed to load inquiries.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const prev = inquiries;
    setInquiries((p) => p.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      await api.patch(`/admin/inquiries/${id}?status=${status}`, {}, { headers: { "X-Admin-Key": key } });
    } catch (e) {
      setInquiries(prev);
      setError("Failed to update status. Please try again.");
    }
  };

  if (!authed) {
    return (
      <div data-testid="admin-page">
        <PageHeader eyebrow="Admin" title="Inquiry Dashboard" sub="Enter your admin key to review incoming consultation requests." />
        <section className="mx-auto max-w-md px-5 pb-24">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
            <div className="flex items-center gap-3 text-white">
              <Lock className="h-5 w-5 text-brand" />
              <h3 className="font-heading text-lg font-semibold">Admin Access</h3>
            </div>
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(key)}
              placeholder="Admin key"
              className="mt-6 w-full rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
            {error && <p className="mt-2 text-xs text-red-400" data-testid="admin-error">{error}</p>}
            <div className="mt-5">
              <GradientButton href="#" icon={false} className="w-full" onClick={(e) => { e.preventDefault(); load(key); }} data-testid="admin-login">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking...</> : "View Inquiries"}
              </GradientButton>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <PageHeader eyebrow="Admin" title="Inquiry Dashboard" sub="Every consultation request submitted through the website." />
      <section className="mx-auto max-w-6xl px-5 pb-24 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Inbox className="h-5 w-5 text-brand" />
            <span className="font-heading text-lg font-semibold">{inquiries.length} inquiries</span>
          </div>
          <button onClick={() => load(key)} data-testid="admin-refresh" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white hover:bg-white/5">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {inquiries.length === 0 ? (
          <p className="rounded-3xl border border-white/10 bg-white/[0.03] p-12 text-center text-muted-foreground">No inquiries yet.</p>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inq, i) => (
              <motion.div
                key={inq.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`inquiry-${inq.id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h4 className="font-heading text-base font-semibold text-white">{inq.name}</h4>
                      <Badge className={STATUS_COLORS[inq.status] || STATUS_COLORS.new}>{inq.status}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{inq.company || "—"} · <a className="hover:text-brand-gold" href={`mailto:${inq.email}`}>{inq.email}</a>{inq.phone ? ` · ${inq.phone}` : ""}</p>
                  </div>
                  <select
                    data-testid={`status-select-${inq.id}`}
                    value={inq.status}
                    onChange={(e) => updateStatus(inq.id, e.target.value)}
                    className="rounded-lg border border-white/15 bg-ink-soft px-3 py-1.5 text-sm text-white focus:outline-none"
                  >
                    {["new", "contacted", "won", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="mt-4 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                  <p><span className="text-white/40">Services:</span> {inq.services?.join(", ") || "—"}</p>
                  <p><span className="text-white/40">Budget:</span> {inq.budget || "—"}</p>
                  <p><span className="text-white/40">Timeline:</span> {inq.timeline || "—"}</p>
                  <p><span className="text-white/40">Business:</span> {inq.business_type || "—"}</p>
                </div>
                {inq.description && <p className="mt-3 border-t border-white/10 pt-3 text-sm text-white/80">{inq.description}</p>}
                <p className="mt-3 text-xs text-white/30">{new Date(inq.created_at).toLocaleString()}</p>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
